import { createContext, useContext, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Calendar, X } from "lucide-react";

const DateRangeContext = createContext(null);

export function useDateRange() {
  return useContext(DateRangeContext);
}

const MONTHS = [
  "Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"
];
const YEARS = ["2022","2023","2024","2025","2026"];

export function DateRangeProvider({ children }) {
  const [fromMonth, setFromMonth] = useState(null);
  const [toMonth, setToMonth] = useState(null);

  const filterData = (data, dateKey = "month") => {
    if (!fromMonth && !toMonth) return data;
    return data.filter((item) => {
      const val = item[dateKey] || item.cohort || item.month;
      if (!val) return true;
      if (fromMonth && val < fromMonth) return false;
      if (toMonth && val > toMonth) return false;
      return true;
    });
  };

  const isActive = fromMonth || toMonth;

  return (
    <DateRangeContext.Provider value={{ fromMonth, toMonth, setFromMonth, setToMonth, filterData, isActive }}>
      {children}
    </DateRangeContext.Provider>
  );
}

export default function DateRangeFilter() {
  const { fromMonth, toMonth, setFromMonth, setToMonth, isActive } = useDateRange();

  const monthOptions = [];
  YEARS.forEach((y) => {
    const maxM = y === "2026" ? 1 : 12;
    for (let m = 1; m <= maxM; m++) {
      const key = `${y}-${String(m).padStart(2, "0")}`;
      monthOptions.push({ value: key, label: `${MONTHS[m - 1]} ${y}` });
    }
  });

  const clear = () => { setFromMonth(null); setToMonth(null); };

  return (
    <div className="flex items-center gap-2" data-testid="date-range-filter">
      <Calendar className="w-3.5 h-3.5 text-slate-500" />
      <Select value={fromMonth || ""} onValueChange={(v) => setFromMonth(v || null)}>
        <SelectTrigger className="w-[120px] h-8 text-xs bg-slate-900/80 border-slate-700 text-slate-300" data-testid="date-from-select">
          <SelectValue placeholder="From" />
        </SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-700 max-h-56">
          {monthOptions.map((o) => (
            <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-slate-600 text-xs">to</span>
      <Select value={toMonth || ""} onValueChange={(v) => setToMonth(v || null)}>
        <SelectTrigger className="w-[120px] h-8 text-xs bg-slate-900/80 border-slate-700 text-slate-300" data-testid="date-to-select">
          <SelectValue placeholder="To" />
        </SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-700 max-h-56">
          {monthOptions.map((o) => (
            <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isActive && (
        <Button variant="ghost" size="sm" onClick={clear} className="h-7 w-7 p-0 text-slate-500 hover:text-slate-300" data-testid="date-clear-btn">
          <X className="w-3.5 h-3.5" />
        </Button>
      )}
    </div>
  );
}
